import { useRef } from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '@/hooks/useScrollReveal';

const AboutSnippet = () => {
  const ref = useRef<HTMLDivElement>(null);
  useScrollReveal(ref);

  return (
    <section id="about-section" ref={ref} className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Section label */}
        <div data-reveal data-delay="1" className="flex items-center gap-3 mb-12">
          <span className="text-[11px] font-bold text-bteal tracking-widest uppercase">01 — Who We Are</span>
          <div className="flex-1 h-px bg-gray-100" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12 md:gap-16 items-start">

          {/* LEFT: Stats stacked — slides in from left */}
          <div className="space-y-6">
            {[
              { value: '22', label: 'Member Distilleries', color: '#28a99e' },
              { value: '18+', label: 'Years of Advocacy', color: '#008000' },
              { value: '20+', label: 'Districts Covered', color: '#F0C808' },
              { value: '₹35,000Cr+', label: 'Investment Facilitated', color: '#28a99e' },
            ].map((s, i) => (
              <div
                key={i}
                data-reveal="left"
                data-delay={String(i + 1)}
                className="pb-6 border-b border-gray-100 last:border-0 last:pb-0"
              >
                <p className="text-3xl font-bold text-ink leading-none mb-1" style={{ color: s.color }}>{s.value}</p>
                <p className="text-ink-muted text-xs">{s.label}</p>
              </div>
            ))}
          </div>

          {/* RIGHT: Text — slides in from right */}
          <div>
            <h2
              data-reveal="right"
              data-delay="1"
              className="text-3xl md:text-4xl font-bold text-ink leading-tight tracking-tight mb-6"
            >
              The unified voice of Bihar&apos;s ethanol industry
            </h2>

            <p data-reveal data-delay="2" className="text-ink-muted text-base leading-relaxed mb-4">
              Bihar Ethanol Association (BEA) was established to serve as the single platform for ethanol manufacturers across Bihar — working at the intersection of private industry, state government, and central policy.
            </p>

            <p data-reveal data-delay="3" className="text-ink-muted text-base leading-relaxed mb-10">
              With Bihar emerging as a key contributor to India&apos;s E20 blending mission, BEA facilitates OMC procurement, represents members before the Department of Industries, and shapes state ethanol policy for a greener future.
            </p>

            {/* Pillars */}
            <div data-reveal data-delay="4" className="space-y-3 mb-10">
              {[
                { icon: 'ri-shield-check-line', label: 'Policy Advocacy', desc: 'Representing members before state & central bodies.' },
                { icon: 'ri-link-m', label: 'Industry Connect', desc: 'Facilitating procurement with IOCL, BPCL & HPCL.' },
                { icon: 'ri-line-chart-line', label: 'Market Intelligence', desc: 'Data-driven insights on pricing and blending mandates.' },
              ].map((p, i) => (
                <div key={i} className="flex items-start gap-3 py-3 border-b border-gray-100">
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-bteal/8 flex-shrink-0 mt-0.5">
                    <i className={`${p.icon} text-bteal text-xs`} />
                  </span>
                  <div>
                    <p className="text-ink font-semibold text-sm">{p.label}</p>
                    <p className="text-ink-muted text-xs mt-0.5">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div data-reveal data-delay="5">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-sm font-semibold text-bteal hover:text-bteal-dark transition-colors duration-150 cursor-pointer"
              >
                Discover Our Story
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSnippet;
